// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient } from '@medplum/core';
import { createReference } from '@medplum/core';
import type { ContactPoint, Extension, Organization, Patient, Practitioner } from '@medplum/fhirtypes';
import { NPI_SYSTEM, PATIENT_EXTENSION_URLS } from '../constants/patient-profile';
import { withAuxiliaryOrgTag, withTenantScope } from './tenant';

export interface ReferringProviderData {
  practitioner: Practitioner | undefined;
  organization: Organization | undefined;
}

const REFERRING_PROVIDER_FIELDS = {
  practiceName: 'practice-name',
  practiceAddress: 'practice-address',
  email: 'email',
  phone: 'phone',
  fax: 'fax',
} as const;

type ReferringProviderField = (typeof REFERRING_PROVIDER_FIELDS)[keyof typeof REFERRING_PROVIDER_FIELDS];

function getReferringProviderField(patient: Patient, field: ReferringProviderField): string {
  return (
    patient.extension
      ?.find((extension) => extension.url === PATIENT_EXTENSION_URLS.referringProvider)
      ?.extension?.find((extension) => extension.url === field)?.valueString ?? ''
  );
}

function buildReferralOrganization(
  practiceName: string,
  practiceAddress: string,
  email: string,
  phone: string,
  fax: string
): Organization {
  let telecom: ContactPoint[] = [];
  telecom = setTelecomEntry(telecom, 'email', email);
  telecom = setTelecomEntry(telecom, 'phone', phone);
  telecom = setTelecomEntry(telecom, 'fax', fax);

  return {
    resourceType: 'Organization',
    name: practiceName || undefined,
    address: practiceAddress ? [{ text: practiceAddress }] : undefined,
    telecom,
  };
}

function getReferringOrganizationReference(patient: Patient): string | undefined {
  return patient.extension?.find((extension) => extension.url === PATIENT_EXTENSION_URLS.referringOrganization)
    ?.valueReference?.reference;
}

function setReferringOrganizationReference(
  extensions: Extension[] | undefined,
  organization: Organization
): Extension[] {
  const otherExtensions =
    extensions?.filter(
      (extension) =>
        extension.url !== PATIENT_EXTENSION_URLS.referringOrganization &&
        extension.url !== PATIENT_EXTENSION_URLS.referringProvider
    ) ?? [];

  return [
    ...otherExtensions,
    {
      url: PATIENT_EXTENSION_URLS.referringOrganization,
      valueReference: createReference(organization),
    },
  ];
}

function buildOrganizationFromPatient(
  patient: Patient,
  legacyOrganization: Organization | undefined
): Organization | undefined {
  const practiceName = getReferringProviderField(patient, REFERRING_PROVIDER_FIELDS.practiceName);
  const practiceAddress = getReferringProviderField(patient, REFERRING_PROVIDER_FIELDS.practiceAddress);
  const email = getReferringProviderField(patient, REFERRING_PROVIDER_FIELDS.email);
  const phone = getReferringProviderField(patient, REFERRING_PROVIDER_FIELDS.phone);
  const fax = getReferringProviderField(patient, REFERRING_PROVIDER_FIELDS.fax);

  if (!practiceName && !practiceAddress && !email && !phone && !fax) {
    return legacyOrganization?.name === 'Polaris Sleep' ? undefined : legacyOrganization;
  }

  return buildReferralOrganization(practiceName, practiceAddress, email, phone, fax);
}

export async function fetchReferringProvider(medplum: MedplumClient, patient: Patient): Promise<ReferringProviderData> {
  const practitionerRef = patient.generalPractitioner?.[0]?.reference;
  const organizationRef = getReferringOrganizationReference(patient) ?? patient.managingOrganization?.reference;

  const [practitioner, organization] = await Promise.all([
    practitionerRef?.startsWith('Practitioner/')
      ? medplum.readReference<Practitioner>({ reference: practitionerRef }).catch(() => undefined)
      : Promise.resolve(undefined),
    organizationRef?.startsWith('Organization/')
      ? medplum.readReference<Organization>({ reference: organizationRef }).catch(() => undefined)
      : Promise.resolve(undefined),
  ]);

  return { practitioner, organization: buildOrganizationFromPatient(patient, organization) };
}

export function getPractitionerNpi(practitioner: Practitioner | undefined): string {
  return practitioner?.identifier?.find((id) => id.system === NPI_SYSTEM)?.value ?? '';
}

export function getTelecomValue(
  resource: Practitioner | Organization | undefined,
  system: ContactPoint['system']
): string {
  return resource?.telecom?.find((t) => t.system === system)?.value ?? '';
}

export function getOrganizationAddressLine(org: Organization | undefined): string {
  if (!org?.address?.[0]) {
    return '';
  }
  const addr = org.address[0];
  const parts = [...(addr.line ?? []), addr.city, addr.state, addr.postalCode].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : (addr.text ?? '');
}

export interface ReferringProviderFormValues {
  title: string;
  firstName: string;
  lastName: string;
  practiceName: string;
  practiceAddress: string;
  npi: string;
  email: string;
  phone: string;
  fax: string;
}

function setTelecomEntry(
  telecom: ContactPoint[] | undefined,
  system: ContactPoint['system'],
  value: string
): ContactPoint[] {
  const others = telecom?.filter((t) => t.system !== system) ?? [];
  return value ? [...others, { system, value }] : others;
}

function setNpi(identifier: Practitioner['identifier'], npi: string): Practitioner['identifier'] {
  const others = identifier?.filter((id) => id.system !== NPI_SYSTEM) ?? [];
  return npi ? [...others, { system: NPI_SYSTEM, value: npi }] : others;
}

export interface SaveReferringProviderResult {
  patient: Patient;
  practitioner: Practitioner;
  organization: Organization;
}

/**
 * Saves referring provider text on the Patient and keeps Patient.managingOrganization
 * unchanged because that reference identifies the patient's tenant organization.
 */
export async function saveReferringProvider(
  medplum: MedplumClient,
  patient: Patient,
  existing: ReferringProviderData,
  values: ReferringProviderFormValues,
  selectedOrganization?: Organization
): Promise<SaveReferringProviderResult> {
  const practitionerInput: Practitioner = {
    ...(existing.practitioner ?? { resourceType: 'Practitioner' }),
    name: [
      {
        ...existing.practitioner?.name?.[0],
        prefix: values.title ? [values.title] : undefined,
        given: values.firstName ? [values.firstName] : undefined,
        family: values.lastName || undefined,
      },
    ],
    telecom: setTelecomEntry(existing.practitioner?.telecom, 'email', values.email),
    identifier: setNpi(existing.practitioner?.identifier, values.npi),
  };

  const savedPractitioner = existing.practitioner?.id
    ? await medplum.updateResource({ ...practitionerInput, id: existing.practitioner.id })
    : await medplum.createResource<Practitioner>(withTenantScope(practitionerInput));

  const organizationInput = buildReferralOrganization(
    values.practiceName.trim(),
    values.practiceAddress,
    values.email,
    values.phone,
    values.fax
  );
  let savedOrganization: Organization;
  if (selectedOrganization?.id) {
    savedOrganization = selectedOrganization;
  } else if (existing.organization?.id) {
    savedOrganization = await medplum.updateResource({ ...organizationInput, id: existing.organization.id });
  } else {
    savedOrganization = await medplum.createResource<Organization>(
      withTenantScope(withAuxiliaryOrgTag(organizationInput))
    );
  }

  const nextPatient: Patient = {
    ...patient,
    generalPractitioner: [createReference(savedPractitioner)],
    extension: setReferringOrganizationReference(patient.extension, savedOrganization),
  };
  const savedPatient = await medplum.updateResource(nextPatient);

  return { patient: savedPatient, practitioner: savedPractitioner, organization: savedOrganization };
}
